/**
 * @fileoverview API layer for Carbon Footprinting. All endpoint knowledge for
 * this feature lives here; hooks and components call these named functions and
 * never assemble URLs or query params themselves.
 */
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";

const COMPANY = config.POSTLOGIN_API_URL_COMPANY;

/**
 * Build the shared scope-summary query params, so every caller sends the same
 * shape (omitting empty filters rather than passing blanks).
 * @param {{ scope: string, locationIds?: Array<number|string>, categories?: string[] }} opts
 * @returns {object} query params common to the single and bulk summary calls
 */
const scopeSummaryParams = ({ scope, locationIds = [], categories = [] }) => {
  const locCsv = (locationIds || []).join(",");
  const catCsv = (categories || []).join(",");
  return {
    scope,
    ...(locCsv ? { locationIds: locCsv } : {}),
    ...(catCsv ? { category: catCsv } : {}),
  };
};

/**
 * Scope summary for one financial year.
 * @param {{ scope: string, fyid: number|string, locationIds?: Array<number|string>, categories?: string[] }} opts
 * @returns {Promise} apiCall result — `data` is keyed by scope
 */
export const getScopeSummary = ({ scope, fyid, locationIds, categories }) =>
  apiCall(
    `${COMPANY}quantative-data/scope-summary`,
    {},
    { ...scopeSummaryParams({ scope, locationIds, categories }), fyid },
    "GET",
  );

/**
 * Scope summary for several financial years in ONE request.
 * @param {{ scope: string, fyIds: Array<number|string>, locationIds?: Array<number|string>, categories?: string[] }} opts
 * @returns {Promise} apiCall result — `data` is keyed by financial year id
 */
export const getScopeSummariesForFys = ({ scope, fyIds = [], locationIds, categories }) =>
  apiCall(
    `${COMPANY}quantative-data/scope-summary/bulk`,
    {},
    { ...scopeSummaryParams({ scope, locationIds, categories }), fyids: fyIds.join(",") },
    "GET",
  );

/**
 * Company locations (sources) used by the carbon-footprint selectors.
 * @returns {Promise} apiCall result
 */
export const getSources = () => apiCall(`${COMPANY}getSource`, {}, {}, "GET");
